import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { ADMIN_INTEGRATIONS_HEALTH_QUERY_KEY } from './useAdminIntegrationsHealthQuery';

export interface IIntegrationRetryTarget {
	accountId: string;
	provider: Api.IntegrationProvider;
}

export type IntegrationRetryOutcome =
	/** Never returned - the zero value exists so a result built without an outcome cannot read as success. */
	| 'Unknown'
	| 'Retried'
	| 'InvalidRequest'
	| 'SkippedNeedsConfiguration'
	| 'SkippedAccountIneligible'
	| 'SkippedAlreadyRunning'
	| 'SkippedDuplicate'
	| 'SkippedDisabled'
	| 'NotFound'
	| 'Failed';

export interface IIntegrationRetryResult {
	accountId: string;
	/** Null where the request row omitted one, which the server refuses rather than binding to a default. */
	provider: Api.IntegrationProvider | null;
	outcome: IntegrationRetryOutcome;
	message: string;
}

/** The endpoint rejects an oversized batch outright rather than truncating it, so send it in chunks. */
const MAX_TARGETS_PER_REQUEST = 100;

export function useAdminIntegrationsHealthRetryMutation({
	onSuccess,
}: {
	onSuccess?: (results: IIntegrationRetryResult[]) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: async (targets: IIntegrationRetryTarget[]) => {
			const results: IIntegrationRetryResult[] = [];
			for (let i = 0; i < targets.length; i += MAX_TARGETS_PER_REQUEST) {
				const batch = await userSession.webServiceHelper.callAsync<IIntegrationRetryResult[]>(
					'admin/integrationsHealth/retry',
					'POST',
					targets.slice(i, i + MAX_TARGETS_PER_REQUEST)
				);
				results.push(...batch);
			}
			return results;
		},
		onSuccess: results => {
			reactQueryClient.invalidateQueries({ queryKey: [ADMIN_INTEGRATIONS_HEALTH_QUERY_KEY] });
			onSuccess?.(results);
		},
		// No MutationCache is configured, so meta.toastErrorMessage would be swallowed.
		onError: () => toast.error("Couldn't retry the selected integrations. Please try again."),
	});
}
